/**
 * useContactAssignments — mapa {phone: assigned_to} do workspace.
 *
 * Carrega o estado inicial via GET /api/contacts/assignments e mantém em
 * tempo real pelo evento SSE CONTACT_ASSIGNMENT_CHANGED (propagado a todos
 * os membros). Usado pelos chat lists para a tag de responsável e pelo
 * drawer do contato.
 */
import { useCallback, useEffect, useState } from 'react';
import assignmentService from '../services/global/assignmentService';
import requestCache from '../services/global/requestCache';
import { useSSEEvent, SSE_EVENTS } from '../contexts/SSEContext';
import { brPhoneVariants, normalizePhone } from '../utils/phone';

// Cache curto (30s) + dedup em voo pelo requestCache: o hook e montado em mais
// de um lugar ao mesmo tempo (Inbox + ChatWindow) e remontado a cada troca de
// aba, e cada montagem refazia GET /contacts/assignments (2,7s para o admin
// com 19 mil contatos). O mapa muda por SSE CONTACT_ASSIGNMENT_CHANGED, que
// tambem invalida o cache abaixo; a troca de workspace recarrega a pagina
// (e o logout chama clearAll), entao o cache nunca sobrevive a outra conta.
const ASSIGNMENTS_CACHE_KEY = 'contacts:assignments';
function loadAssignmentsShared() {
  return requestCache.cachedGet(
    ASSIGNMENTS_CACHE_KEY,
    async () => {
      const res = await assignmentService.getAll();
      return res?.data || {};
    },
    { ttl: 30_000 },
  );
}

export default function useContactAssignments() {
  const [assignments, setAssignments] = useState({});
  // `loading` distingue "ainda não sei" de "não tem responsável". Sem isso o
  // chat list tratava o mapa vazio do primeiro render como "sem responsável em
  // nenhuma linha" e pintava o chip do AGENTE em todas — o mapa da company
  // leva ~2,7s para o admin (19 mil contatos), então o usuário via o chip
  // errado esse tempo todo e depois ele trocava sozinho.
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    loadAssignmentsShared()
      .then((data) => {
        if (!cancelled) { setAssignments(data || {}); setLoading(false); }
      })
      .catch((err) => {
        console.warn('[useContactAssignments] load failed:', err);
        // Falhou: não deixa o chat list preso em "carregando" para sempre —
        // volta ao comportamento antigo (chip do agente) em vez de linha muda.
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  useSSEEvent(
    SSE_EVENTS.CONTACT_ASSIGNMENT_CHANGED,
    useCallback((payload) => {
      const phone = normalizePhone(payload?.phone);
      if (!phone) return;
      // O mapa em cache ficou velho: a proxima montagem busca de novo em vez
      // de servir o responsavel anterior deste contato.
      requestCache.invalidate(ASSIGNMENTS_CACHE_KEY);
      setAssignments((prev) => {
        const next = { ...prev };
        // Grava em TODAS as variantes do nono dígito: a linha do chat pode
        // estar chaveada pela forma irmã (ver utils/phone).
        brPhoneVariants(phone).forEach((v) => {
          if (payload?.assigned_to) next[v] = payload.assigned_to;
          else delete next[v];
        });
        return next;
      });
    }, [])
  );

  const getAssignment = useCallback(
    (phone) => {
      const digits = normalizePhone(phone);
      if (!digits) return null;
      // Procura nas duas formas: o backend indexa o mapa pelo telefone do doc,
      // que pode diferir do da linha só pelo nono dígito.
      for (const v of brPhoneVariants(digits)) {
        if (assignments[v]) return assignments[v];
      }
      return null;
    },
    [assignments]
  );

  return { assignments, getAssignment, loading };
}
